// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as BoostSubscriptionCommands from '@app/features/boost/commands/BoostSubscriptionCommands';
import {BoostQuantityStep, BoostSteps} from '@app/features/boost/components/BoostQuantityStep';
import styles from '@app/features/boost/components/BoostSubscription.module.css';
import BoostSubscription from '@app/features/boost/state/BoostSubscription';
import GuildBoosts from '@app/features/boost/state/GuildBoosts';
import {boostSubscriptionRoute} from '@app/features/boost/utils/GuildBoostPurchase';
import {
	BACK_DESCRIPTOR,
	CANCEL_DESCRIPTOR,
	CLOSE_DESCRIPTOR,
	CONTINUE_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {failureCode, failureContext} from '@app/features/platform/utils/ResponseInspection';
import {type ShopBoostHere, ShopCheckoutModal} from '@app/features/shop/components/modals/ShopCheckoutModal';
import {BOOST_LIMIT_DESCRIPTOR, BOOST_LIMIT_TITLE_DESCRIPTOR} from '@app/features/shop/utils/ShopMessageDescriptors';
import {formatPayable} from '@app/features/shop/utils/ShopPriceFormat';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {getFormattedLongDate} from '@fluxer/date_utils/src/DateFormatting';
import type {
	PaddleBoostPreviewResponse,
	PaddleBoostSubscriptionState,
} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Manage boosts',
	comment: 'Title of the dialog that changes how many boosts a monthly boost subscription holds.',
});
const CHOOSE_STEP_DESCRIPTOR = msg({
	message: 'Choose how many',
	comment: 'First step name at the top of the boost subscription dialog: picking the number of boosts.',
});
const CONFIRM_DESCRIPTOR = msg({
	message: 'Confirm',
	comment: 'Second step name and the button that changes the number of boosts of the boost subscription.',
});
const RENEWS_DESCRIPTOR = msg({
	message: "Boosts are a renewing subscription. You can cancel any time on a community's Boost page.",
	comment: 'Note in the dialog that changes the number of boosts of a monthly boost subscription.',
});
const CANT_CHANGE_DESCRIPTOR = msg({
	message: "The number of boosts can't be changed right now.",
	comment:
		'Shown in the boost subscription dialog when the server does not allow a change now (a payment problem, a cancellation, a review).',
});
const TODAY_DESCRIPTOR = msg({
	message: 'Today',
	comment: 'Row label in the boost subscription change summary: what Paddle charges at once.',
});
const NO_CHARGE_DESCRIPTOR = msg({
	message: 'No charge',
	comment: 'Row value in the boost subscription change summary when nothing is charged today.',
});
const FROM_NEXT_DESCRIPTOR = msg({
	message: 'Every month from the next payment',
	comment: 'Row label in the boost subscription change summary: the new monthly amount.',
});
const NEXT_PAYMENT_DESCRIPTOR = msg({
	message: 'Next payment: {date}',
	comment: 'Line in the boost subscription change summary. {date} is a long date.',
});
const REMOVED_LAST_DESCRIPTOR = msg({
	message:
		'{count, plural, one {The removed boost lasts} other {The # removed boosts last}} until {date}. The most recently added boosts go first.',
	comment:
		'Boost subscription change summary when the number goes down: the removed boosts stay until the paid period ends ({date}, a long date); no money comes back.',
});
const CHANGED_DESCRIPTOR = msg({
	message: '{count, plural, one {Your subscription now holds # boost.} other {Your subscription now holds # boosts.}}',
	comment: 'Toast after the number of boosts of the boost subscription changed.',
});
const CHANGE_REFUSED_DESCRIPTOR = msg({
	message:
		"Paddle didn't accept this change now. Your card may have been declined, or the renewal is too close. Please try again later.",
	comment: 'Shown in the boost subscription dialog when Paddle refused the change of the number of boosts.',
});
const CHARGE_CHANGED_DESCRIPTOR = msg({
	message: 'The amounts changed. Check them and confirm again.',
	comment:
		'Shown on the boost subscription change summary when the charge changed since it was shown; the new amounts are on screen.',
});
const SUBSCRIPTION_TITLE_DESCRIPTOR = msg({
	message: 'Boost subscription',
	comment: 'Title of the boost subscription dialog while the subscription is read, or when reading it failed.',
});
const LOAD_FAILED_DESCRIPTOR = msg({
	message: "We couldn't load your boost subscription.",
	comment: 'Shown in the boost subscription dialog when reading the subscription failed; a button tries again.',
});
const SALES_CLOSED_DESCRIPTOR = msg({
	message: 'Boost sales are closed right now.',
	comment: 'Shown in the boost subscription dialog when the server refuses a change because boosts do not sell now.',
});
const NOT_ACTIVE_DESCRIPTOR = msg({
	message: 'Your boost subscription is no longer active.',
	comment: 'Shown in the boost subscription dialog when the server finds no active boost subscription.',
});

function failureText(error: unknown, i18n: ReturnType<typeof useLingui>['i18n']): string {
	switch (failureCode(error)) {
		case APIErrorCodes.SHOP_SUBSCRIPTION_CHANGE_REFUSED:
			return i18n._(CHANGE_REFUSED_DESCRIPTOR);
		case APIErrorCodes.NO_ACTIVE_SUBSCRIPTION:
			return i18n._(NOT_ACTIVE_DESCRIPTOR);
		case APIErrorCodes.SHOP_RECURRING_NOT_ELIGIBLE:
			return i18n._(SALES_CLOSED_DESCRIPTOR);
		case APIErrorCodes.MAX_PURCHASED_BOOST_SLOTS: {
			const limit = failureContext(error)?.max_purchased_boost_slots;
			return typeof limit === 'number'
				? i18n._(BOOST_LIMIT_DESCRIPTOR, {count: limit})
				: i18n._(BOOST_LIMIT_TITLE_DESCRIPTOR);
		}
		default:
			return i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR);
	}
}

// A new quantity for the subscription: Paddle's preview first, the change only on confirm. Proration is Paddle's.
const BoostQuantityChange = observer(({initial}: {initial: PaddleBoostSubscriptionState}) => {
	const {i18n} = useLingui();
	const subscription = BoostSubscription.subscription ?? initial;
	const [quantity, setQuantity] = useState(initial.quantity);
	const [preview, setPreview] = useState<PaddleBoostPreviewResponse | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [chargeChanged, setChargeChanged] = useState(false);
	const date = (iso: string | null) => (iso ? getFormattedLongDate(new Date(iso), i18n.locale) : null);
	const steps = [i18n._(CHOOSE_STEP_DESCRIPTOR), i18n._(CONFIRM_DESCRIPTOR)];

	const run = async (work: () => Promise<void>) => {
		if (busy) return;
		setBusy(true);
		setError(null);
		setChargeChanged(false);
		try {
			await work();
		} catch (failure) {
			setError(failureText(failure, i18n));
			const code = failureCode(failure);
			if (code === APIErrorCodes.NO_ACTIVE_SUBSCRIPTION || code === APIErrorCodes.MAX_PURCHASED_BOOST_SLOTS) {
				void BoostSubscription.load();
			}
		} finally {
			setBusy(false);
		}
	};
	const review = () =>
		run(async () => {
			setPreview(await BoostSubscriptionCommands.previewBoostQuantity(quantity));
		});
	const confirm = (confirmed: PaddleBoostPreviewResponse) =>
		run(async () => {
			let after: PaddleBoostSubscriptionState;
			try {
				after = await BoostSubscriptionCommands.changeBoostQuantity(
					confirmed.quantity,
					confirmed.charge_now_minor !== null,
				);
			} catch (failure) {
				if (failureCode(failure) !== APIErrorCodes.SHOP_RECURRING_QUOTE_CHANGED) throw failure;
				// The charge moved since the preview (removed boosts ran out, a renewal came): the new one is confirmed anew.
				setPreview(await BoostSubscriptionCommands.previewBoostQuantity(confirmed.quantity));
				setChargeChanged(true);
				return;
			}
			BoostSubscription.set(after);
			void GuildBoosts.loadSlots();
			ToastCommands.success(i18n._(CHANGED_DESCRIPTOR, {count: after.quantity}));
			ModalCommands.pop();
		});
	const alert = error && (
		<p className={styles.alert} role="alert" data-flx="boost.subscription-modal.error">
			{error}
		</p>
	);

	let body: React.ReactNode;
	let footer: React.ReactNode;
	if (preview) {
		const removed = subscription.quantity - preview.quantity;
		const paidUntil = date(subscription.paid_until);
		const nextPayment = date(preview.next_billed_at);
		body = (
			<div className={styles.step} data-flx="boost.subscription-modal.confirm">
				<BoostSteps steps={steps} current={1} />
				<dl className={styles.rows} data-flx="boost.subscription-modal.preview">
					<div className={styles.row}>
						<dt>{i18n._(TODAY_DESCRIPTOR)}</dt>
						<dd>
							{preview.charge_now_minor === null
								? i18n._(NO_CHARGE_DESCRIPTOR)
								: formatPayable(preview.charge_now_minor, preview.currency, i18n.locale)}
						</dd>
					</div>
					<div className={styles.row}>
						<dt>{i18n._(FROM_NEXT_DESCRIPTOR)}</dt>
						<dd>{formatPayable(preview.recurring_amount_minor, preview.currency, i18n.locale)}</dd>
					</div>
				</dl>
				{nextPayment && <p className={styles.body}>{i18n._(NEXT_PAYMENT_DESCRIPTOR, {date: nextPayment})}</p>}
				{removed > 0 && paidUntil && (
					<p className={styles.body} data-flx="boost.subscription-modal.removed">
						{i18n._(REMOVED_LAST_DESCRIPTOR, {count: removed, date: paidUntil})}
					</p>
				)}
				{chargeChanged && (
					<p className={styles.alert} role="alert" data-flx="boost.subscription-modal.charge-changed">
						{i18n._(CHARGE_CHANGED_DESCRIPTOR)}
					</p>
				)}
				{alert}
			</div>
		);
		footer = (
			<>
				<Button
					variant="secondary"
					disabled={busy}
					onClick={() => setPreview(null)}
					data-flx="boost.subscription-modal.button.back"
				>
					{i18n._(BACK_DESCRIPTOR)}
				</Button>
				<Button
					submitting={busy}
					onClick={() => void confirm(preview)}
					data-flx="boost.subscription-modal.button.confirm"
				>
					{i18n._(CONFIRM_DESCRIPTOR)}
				</Button>
			</>
		);
	} else {
		const locked = !subscription.can_change_quantity;
		body = (
			<BoostQuantityStep
				quantity={quantity}
				max={Math.max(BoostSubscription.maxQuantity, subscription.quantity)}
				unitMinor={subscription.unit_amount_minor}
				currency={subscription.currency}
				onChange={(next) => {
					setQuantity(next);
					setError(null);
				}}
				disabled={busy || locked}
				steps={steps}
			>
				{locked && <p className={styles.body}>{i18n._(CANT_CHANGE_DESCRIPTOR)}</p>}
				{alert}
				<div className={styles.notes}>
					<p className={styles.note}>{i18n._(RENEWS_DESCRIPTOR)}</p>
					{subscription.next_billed_at && (
						<p className={styles.note}>{i18n._(NEXT_PAYMENT_DESCRIPTOR, {date: date(subscription.next_billed_at)})}</p>
					)}
				</div>
			</BoostQuantityStep>
		);
		footer = (
			<>
				<Button
					variant="secondary"
					onClick={() => ModalCommands.pop()}
					data-flx="boost.subscription-modal.button.cancel"
				>
					{i18n._(CANCEL_DESCRIPTOR)}
				</Button>
				<Button
					submitting={busy}
					disabled={locked || quantity === subscription.quantity}
					onClick={() => void review()}
					data-flx="boost.subscription-modal.button.continue"
				>
					{i18n._(CONTINUE_DESCRIPTOR)}
				</Button>
			</>
		);
	}
	return (
		<Modal.Root size="small" centered data-flx="boost.subscription-modal">
			<Modal.Header title={i18n._(TITLE_DESCRIPTOR)} />
			<Modal.Content>{body}</Modal.Content>
			<Modal.Footer>{footer}</Modal.Footer>
		</Modal.Root>
	);
});

// While the subscription is read, and when reading it failed: a failure is never shown as "no room for a boost".
const SubscriptionStatus = observer(() => {
	const {i18n} = useLingui();
	const failed = BoostSubscription.status === 'failed';
	return (
		<Modal.Root size="small" centered data-flx="boost.subscription-modal.status">
			<Modal.Header title={i18n._(SUBSCRIPTION_TITLE_DESCRIPTOR)} />
			<Modal.Content>
				{failed ? (
					<p className={styles.body} role="alert">
						{i18n._(LOAD_FAILED_DESCRIPTOR)}
					</p>
				) : (
					<Spinner />
				)}
			</Modal.Content>
			<Modal.Footer>
				<Button
					variant="secondary"
					onClick={() => ModalCommands.pop()}
					data-flx="boost.subscription-modal.button.close"
				>
					{i18n._(CLOSE_DESCRIPTOR)}
				</Button>
				{failed && (
					<Button onClick={() => void BoostSubscription.load()} data-flx="boost.subscription-modal.button.retry">
						{i18n._(TRY_AGAIN_DESCRIPTOR)}
					</Button>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});

// The boost subscription dialog: a new subscription goes through the shop checkout, a subscription that may change
// changes its quantity here, and beside one that cannot change now a single 30 day slot is sold. Which one is decided
// once the subscription is read, and kept, so a purchase that completes inside stays on its success view; the store's
// numbers (how many boosts fit) are read on every render.
export const BoostSubscriptionModal = observer(({here}: {here?: ShopBoostHere}) => {
	const decided = useRef<ReturnType<typeof boostSubscriptionRoute> | null>(null);
	if (decided.current === null && BoostSubscription.status === 'ready') {
		decided.current = boostSubscriptionRoute(BoostSubscription.subscription, Date.now());
	}
	const route = decided.current;
	const subscription = BoostSubscription.subscription;
	if (route === null) return <SubscriptionStatus />;
	if (route === 'one_time') return <ShopCheckoutModal target={{kind: 'boost', here}} />;
	if (route === 'change' && subscription) return <BoostQuantityChange initial={subscription} />;
	return (
		<ShopCheckoutModal
			target={{kind: 'boost', here, recurring: {quantity: 1, maxQuantity: BoostSubscription.maxQuantity}}}
		/>
	);
});
